import React, { useEffect, useRef, useState } from "react";
import { loadStyles } from "../../utils/cssLoader";
import { getData } from "../../utils/getData";

/**
 * F1RaceCarousel — F1 race-calendar carousel (Austrian GP / British GP / Belgian GP strip).
 *
 * Calendar comes from formula1/races (sessions/schedules). Podium finish orders come from the
 * per-race endpoint formula1/{id}/races (driver results: pos, nm, team, time, gap, pts) — fetched
 * lazily for the finished races currently in view.
 *   • Windowed 3-up (2 / 1 on narrower widths) with prev/next arrows.
 *   • Finished race → podium (top 5, "Classifica completa" toggles the full order).
 *   • Otherwise → session schedule ("Programma" / "In arrivo"), live session highlighted.
 */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const parseDate = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return isNaN(d.getTime()) ? null : d;
};
const fmtRange = (a, b) => {
  const d1 = parseDate(a);
  if (!d1) return "";
  const d2 = parseDate(b) || d1;
  const m1 = MONTHS[d1.getMonth()];
  if (d1.getMonth() === d2.getMonth()) {
    return d2.getDate() !== d1.getDate()
      ? `${d1.getDate()} - ${d2.getDate()} ${m1}`
      : `${d1.getDate()} ${m1}`;
  }
  return `${d1.getDate()} ${m1} - ${d2.getDate()} ${MONTHS[d2.getMonth()]}`;
};
const fmtTime = (s) => {
  const d = parseDate(s);
  if (!d) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${MONTHS[d.getMonth()]} ${hh}:${mm}`;
};

const rowsFromRaw = (raw) => {
  const d = raw?.data ?? raw;
  return Array.isArray(d) ? d : [];
};

const mapRace = (race) => {
  const sessions = (race?.race_types || [])
    .filter((s) => s?.status !== "Deleted")
    .map((s) => ({
      id: s?.id,
      name: s?.nm || "",
      datetime: s?.dt,
      status: s?.status,
      act: s?.act,
    }));
  const isCancelled = sessions.length > 0 && sessions.every((s) => s.status === "Cancelled");
  const isFinished = sessions.length > 0 && sessions.every((s) => s.status === "Finished");
  const isUpcoming = sessions.some((s) => s.status === "Not started yet") && !isFinished;
  return {
    id: race?.id,
    name: race?.nm || "",
    startDate: race?.race_start,
    endDate: race?.race_end,
    city: race?.venue?.city || "",
    sessions,
    isCancelled,
    isFinished,
    isUpcoming,
  };
};

const calendarFromRaw = (raw) => {
  const races = rowsFromRaw(raw)
    .map(mapRace)
    .filter((r) => !r.isCancelled && r.sessions.length);
  races.sort(
    (a, b) => (parseDate(a.startDate)?.getTime() || 0) - (parseDate(b.startDate)?.getTime() || 0)
  );
  return races;
};

// Current race = first one not yet finished as of `date` (today), else the last.
const findCurrentIdx = (cal, dateStr) => {
  if (!cal.length) return 0;
  const today = parseDate(dateStr) || new Date();
  const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const i = cal.findIndex((r) => {
    const e = parseDate(r.endDate) || parseDate(r.startDate);
    return e && e.getTime() >= t0;
  });
  return i < 0 ? cal.length - 1 : i;
};

// per-race endpoint rows → podium { position, name, team, time, gap, points }
const mapResults = (raw) =>
  rowsFromRaw(raw)
    .map((e) => ({
      position: e?.pos,
      name: e?.nm || e?.ply || "",
      team: e?.team || "",
      time: e?.time || "",
      gap: e?.gap || "",
      points: e?.pts ?? 0,
    }))
    .filter((r) => r.name)
    .sort((a, b) => (a.position ?? 999) - (b.position ?? 999));

// ── Card ────────────────────────────────────────────────────────────────────
const Card = ({ race, results, current, expanded, dictionary }) => {
  const [showAll, setShowAll] = useState(false);
  const range = fmtRange(race.startDate, race.endDate);
  const hasPodium = race.isFinished && Array.isArray(results) && results.length > 0;
  const podium = hasPodium ? (showAll ? results : results.slice(0, 5)) : [];
  const schedule = [...race.sessions].sort(
    (a, b) => (parseDate(a.datetime)?.getTime() || 0) - (parseDate(b.datetime)?.getTime() || 0)
  );

  return (
    <div className={`f1c__card${current ? " is-current" : ""}${expanded ? " is-expanded" : ""}`}>
      <div className="f1c__card-head">
        <span className="f1c__gp">{race.name}</span>
        <span className="f1c__range">{range}</span>
      </div>

      {expanded &&
        (hasPodium ? (
        <div className="f1c__body">
          <ul className="f1c__podium">
            {podium.map((r, i) => (
              <li key={i} className="f1c__pod">
                <span className="f1c__pod-pos">{r.position ?? i + 1}</span>
                <span className="f1c__pod-name">{r.name}</span>
                <span className="f1c__pod-val">
                  {r.position === 1 ? r.time || `${r.points} pts` : r.gap ? `+ ${r.gap}` : r.time}
                </span>
              </li>
            ))}
          </ul>
          {results.length > 5 ? (
            <button type="button" className="f1c__full" onClick={() => setShowAll((v) => !v)}>
              {showAll
                ? dictionary?.f1StandingsShort || "Show less ‹"
                : dictionary?.f1FullStandings || "Full standings ›"}
            </button>
          ) : null}
        </div>
      ) : schedule.length ? (
        <div className="f1c__body">
          <span className="f1c__sched-label">
            {!current && race.isUpcoming
              ? dictionary?.f1UpcomingLabel || "Upcoming" // later races → "Upcoming" (In arrivo)
              : dictionary?.f1ScheduleLabel || "Schedule"} {/* current race → "Schedule" (Programma) */}
          </span>
          <ul className="f1c__sched-list">
            {schedule.map((s, i) => {
              // Green only for a genuinely live session (by status). act=1 in this feed just
              // flags the "Race" session (all Finished) — not "live" — so we ignore it.
              const live = /live|progress|running/i.test(s.status || "");
              return (
                <li key={i} className={`f1c__sess${live ? " is-live" : ""}`}>
                  <span className="f1c__sess-nm">{s.name}</span>
                  <span className="f1c__sess-dt">{fmtTime(s.datetime)}</span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <div className="f1c__body">
          <div className="f1c__empty">
            {dictionary?.f1NoSessionsScheduled || "No sessions scheduled."}
          </div>
        </div>
        ))}
    </div>
  );
};

const F1RaceCarousel = ({
  serverData = null,
  data: dataProp = null,
  dataId,
  baseUrl,
  sport = "formula1",
  loadCSS = true,
  dataClient = false,
  date = null, // today's date (YYYY-MM-DD) — sent by the server on load
  dictionary,
}) => {
  useEffect(() => {
    loadStyles("f1RaceCarousel");
  }, []);

  const [calendar, setCalendar] = useState(() => calendarFromRaw(serverData || dataProp));
  const [start, setStart] = useState(0);
  const [visible, setVisible] = useState(3);
  const [expanded, setExpanded] = useState(true); // show all card data by default
  const [results, setResults] = useState({}); // raceId -> podium rows
  const rootRef = useRef(null);

  useEffect(() => {
    const c = calendarFromRaw(serverData || dataProp);
    if (c.length) setCalendar(c);
  }, [serverData, dataProp]);

  // Hydration: re-read the serialized payload off the container.
  useEffect(() => {
    if (!dataClient) return;
    try {
      const el = document.querySelector(".microservice-cont");
      const parsed = JSON.parse(el?.getAttribute("data-listingdata") || "null");
      const c = calendarFromRaw(parsed?.data);
      if (c.length) setCalendar(c);
    } catch (_) {}
  }, [dataClient]);

  // Embed: self-fetch the calendar.
  useEffect(() => {
    if (calendar.length || !baseUrl) return;
    let cancelled = false;
    getData({ dataType: "f1-races", baseUrl, sport }).then((res) => {
      if (cancelled) return;
      const c = calendarFromRaw(res);
      if (c.length) setCalendar(c);
    });
    return () => {
      cancelled = true;
    };
  }, [calendar.length, baseUrl, sport]);

  // Responsive: how many cards fit (3 / 2 / 1).
  useEffect(() => {
    const calc = () => {
      const w = rootRef.current?.offsetWidth || 0;
      setVisible(w > 0 ? (w < 560 ? 1 : w < 820 ? 2 : 3) : 3);
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);

  // Centre the window on the current race (by today's date).
  useEffect(() => {
    if (!calendar.length) return;
    const idx = findCurrentIdx(calendar, date);
    setStart(Math.max(0, Math.min(idx - 1, calendar.length - visible)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendar.length]);

  const total = calendar.length;
  const maxStart = Math.max(0, total - visible);
  const s = Math.min(Math.max(0, start), maxStart);

  // Fetch podium results for the finished races currently in view (only when expanded).
  useEffect(() => {
    if (!baseUrl || !calendar.length || !expanded) return;
    const win = calendar.slice(s, s + visible);
    const need = win.filter((r) => r.isFinished && !(r.id in results));
    if (!need.length) return;
    let cancelled = false;
    need.forEach((r) => {
      getData({ dataId: r.id, dataType: "f1-race-results", baseUrl, sport }).then((res) => {
        if (cancelled) return;
        setResults((prev) => ({ ...prev, [r.id]: mapResults(res) }));
      });
    });
    return () => {
      cancelled = true;
    };
  }, [s, visible, calendar, baseUrl, sport, results, expanded]);

  if (!calendar.length) {
    return (
      <div className="f1c f1c--loading">
        <span>Loading calendar…</span>
      </div>
    );
  }

  const win = calendar.slice(s, s + visible);
  const currentIdx = findCurrentIdx(calendar, date);

  return (
    <div className={`f1c${expanded ? " is-expanded" : ""}`} ref={rootRef}>
      <div className="f1c__viewport">
        {total > visible ? (
          <button
            type="button"
            className="f1c__arrow f1c__arrow--prev"
            disabled={s <= 0}
            onClick={() => setStart(s - 1)}
            aria-label="Previous"
          >
            ‹
          </button>
        ) : null}

        <div className="f1c__row">
          {win.map((race, i) => (
            <Card
              key={race.id ?? i}
              race={race}
              results={results[race.id]}
              current={calendar.indexOf(race) === currentIdx}
              expanded={expanded}
              dictionary={dictionary}
            />
          ))}
        </div>

        {total > visible ? (
          <button
            type="button"
            className="f1c__arrow f1c__arrow--next"
            disabled={s >= maxStart}
            onClick={() => setStart(s + 1)}
            aria-label="Next"
          >
            ›
          </button>
        ) : null}
      </div>

      {/* Bottom "Show more / less" pill on a divider line, with a green chevron */}
      <div className="f1c__more-wrap">
        <button
          type="button"
          className="f1c__toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded
            ? dictionary?.f1ShowLess || "Show less"
            : dictionary?.f1ShowMore || "Show more"}
          <span className="f1c__toggle-ic" aria-hidden="true">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 6l4 4 4-4" />
            </svg>
          </span>
        </button>
      </div>
    </div>
  );
};

export default F1RaceCarousel;
